import { Controller } from '@hotwired/stimulus';

/**
 * Compte à rebours d'un code d'appairage (P0b / C1).
 *
 * Écrit `mm:ss` dans le jeton tant que l'échéance n'est pas passée, puis bascule
 * du fragment `running` au fragment `expired` — TOUS DEUX RENDUS PAR LE SERVEUR.
 *
 * ⚠️ AUCUNE COPIE FRANÇAISE ICI, et cette fois par construction : ce contrôleur
 * n'écrit qu'un `mm:ss` et ne fait qu'ajouter/retirer une classe. La phrase
 * d'expiration est du markup serveur, donc relue par `ui-lint-erb.sh` et
 * `lint-terminology.sh`, dont aucun ne lit les `.js`.
 *
 * ⚠️ `hidden` NUE, jamais une utilitaire d'affichage : en Tailwind v4 un `flex`
 * ou `inline-flex` sur le même élément gagne sur `.hidden` selon l'ordre
 * d'émission, et le fragment « caché » resterait visible sans erreur.
 *
 * L'échéance est un instant ABSOLU (ISO 8601 rendu par le serveur) et jamais une
 * durée : une durée serait décomptée depuis le chargement de la page, et un
 * onglet laissé ouvert, mis en veille ou restauré afficherait un temps restant
 * qui n'a plus de rapport avec celui du serveur.
 *
 * @targets output  — le nœud texte qui porte `mm:ss`
 *          running — le fragment « expire dans … »
 *          expired — le fragment de fin de vie (porte l'annonce aria-live)
 * @values  deadline (String, ISO 8601) — l'instant d'expiration
 *
 * @example
 *   <div data-controller="pairing-countdown"
 *        data-pairing-countdown-deadline-value="2026-08-20T10:12:00Z">
 *     <p data-pairing-countdown-target="running">
 *       Expire dans <span data-pairing-countdown-target="output">09:58</span>
 *     </p>
 *     <p data-pairing-countdown-target="expired" class="hidden" aria-live="polite">…</p>
 *   </div>
 */
export default class extends Controller {
  static targets = ['output', 'running', 'expired'];

  static values = {
    deadline: String,
  };

  connect() {
    this.timer = null;
    this.render();
    // Une échéance déjà passée au montage ne démarre aucun intervalle : le
    // premier rendu a déjà basculé sur le fragment de fin de vie.
    if (this.remainingSeconds() > 0) {
      this.timer = setInterval(() => this.render(), 1000);
    }
  }

  disconnect() {
    this.stop();
  }

  render() {
    const remaining = this.remainingSeconds();

    if (remaining <= 0) {
      this.showExpired();
      this.stop();
      return;
    }

    if (this.hasOutputTarget) {
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      this.outputTarget.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
  }

  showExpired() {
    if (this.hasRunningTarget) this.runningTarget.classList.add('hidden');
    if (this.hasExpiredTarget) this.expiredTarget.classList.remove('hidden');
  }

  remainingSeconds() {
    const deadline = Date.parse(this.deadlineValue);
    // Une échéance illisible bascule sur le fragment d'expiration : c'est l'état
    // le moins trompeur, et il ne laisse jamais « NaN:NaN » à l'écran.
    if (Number.isNaN(deadline)) return 0;

    return Math.max(0, Math.floor((deadline - Date.now()) / 1000));
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}
